import { zodResolver } from '@hookform/resolvers/zod';
import { EventType } from '@waitroom/analytics';
import { useVerifyOTP } from '@waitroom/auth';
import { navigateTo } from '@waitroom/common';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../../../../analytics/services';
import validationSchema from './VerifyForm.schema';
import { UseVerifyFormProps, VerifyFormValues } from './VerifyForm.types';

export const useVerifyForm = ({
  onSubmit,
  onSuccess,
  onError,
  email = '',
  redirectUrl,
  clientId,
  state,
  codeChallenge,
  onCompleted,
}: UseVerifyFormProps) => {
  const { t } = useTranslation();
  const schema = useMemo(() => validationSchema(t), [t]);
  const methods = useForm<VerifyFormValues>({
    resolver: zodResolver(schema),
  });
  const { setValue } = methods;

  const mutation = useVerifyOTP({
    onMutate: onSubmit,
    onSuccess: (...args) => {
      onSuccess?.(...args);
      analyticsService.track(EventType.UserVerifyOTP, { success: true });
    },
    onError,
    onRedirect: navigateTo,
  });

  const submit = async (values: VerifyFormValues) => {
    mutation.mutate({
      email,
      otp: values.code,
      redirectUrl,
      clientId,
      state,
      codeChallenge,
      onCompleted,
    });
    return true;
  };

  const updateCode = useCallback((value: string): void => setValue('code', value), [setValue]);

  return { t, methods, submit, mutation, updateCode };
};
